export type DateOfBirthParts = {
  day: string;
  month: string;
  year: string;
};

export const EMPTY_DATE_OF_BIRTH_PARTS: DateOfBirthParts = {
  day: '',
  month: '',
  year: '',
};

function padPart(value: number) {
  return String(value).padStart(2, '0');
}

function parseIsoDateOfBirth(value: string | null | undefined) {
  if (!value) {
    return null;
  }

  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) {
    return null;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
    return null;
  }

  const parsed = new Date(Date.UTC(year, month - 1, day));
  if (
    parsed.getUTCFullYear() !== year
    || parsed.getUTCMonth() !== month - 1
    || parsed.getUTCDate() !== day
  ) {
    return null;
  }

  return { year, month, day };
}

export function sanitizeDatePartInput(value: string, maxLength: number) {
  return value.replace(/\D/g, '').slice(0, maxLength);
}

export function splitDateOfBirthParts(value: string | null | undefined): DateOfBirthParts {
  const parsed = parseIsoDateOfBirth(value);
  if (!parsed) {
    return EMPTY_DATE_OF_BIRTH_PARTS;
  }

  return {
    day: padPart(parsed.day),
    month: padPart(parsed.month),
    year: String(parsed.year),
  };
}

export function calculateAgeFromDateOfBirth(value: string | null | undefined, today = new Date()) {
  const parsed = parseIsoDateOfBirth(value);
  if (!parsed) {
    return null;
  }

  const todayUtc = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  const dobUtc = Date.UTC(parsed.year, parsed.month - 1, parsed.day);
  if (dobUtc > todayUtc) {
    return null;
  }

  let age = today.getFullYear() - parsed.year;
  const hasHadBirthday =
    today.getMonth() > parsed.month - 1
    || (today.getMonth() === parsed.month - 1 && today.getDate() >= parsed.day);

  if (!hasHadBirthday) {
    age -= 1;
  }

  return age >= 0 ? age : null;
}

export function resolveStoredAge(dateOfBirth: string | null | undefined, fallbackAge: number | null | undefined) {
  const derivedAge = calculateAgeFromDateOfBirth(dateOfBirth);
  if (derivedAge != null) {
    return derivedAge;
  }

  if (typeof fallbackAge === 'number' && Number.isFinite(fallbackAge) && fallbackAge > 0) {
    return fallbackAge;
  }

  return null;
}

export function formatUkDateOfBirth(value: string | null | undefined) {
  const parsed = parseIsoDateOfBirth(value);
  if (!parsed) {
    return null;
  }

  return `${padPart(parsed.day)}/${padPart(parsed.month)}/${parsed.year}`;
}

export function formatAgeLabel(dateOfBirth: string | null | undefined, fallbackAge?: number | null) {
  const age = resolveStoredAge(dateOfBirth, fallbackAge);
  return age == null ? null : `Age ${age}`;
}

export function resolveDateOfBirthInput(parts: DateOfBirthParts, today = new Date()) {
  const day = sanitizeDatePartInput(parts.day, 2);
  const month = sanitizeDatePartInput(parts.month, 2);
  const year = sanitizeDatePartInput(parts.year, 4);
  const isBlank = !day && !month && !year;
  const isComplete = day.length === 2 && month.length === 2 && year.length === 4;

  if (isBlank) {
    return {
      age: null,
      error: null,
      isBlank: true,
      isComplete: false,
      isValid: false,
      isoValue: '',
      parts: { day, month, year },
    };
  }

  if (!isComplete) {
    return {
      age: null,
      error: null,
      isBlank: false,
      isComplete: false,
      isValid: false,
      isoValue: '',
      parts: { day, month, year },
    };
  }

  const isoValue = `${year}-${month}-${day}`;
  const parsed = parseIsoDateOfBirth(isoValue);
  if (!parsed) {
    return {
      age: null,
      error: 'Choose a real date.',
      isBlank: false,
      isComplete: true,
      isValid: false,
      isoValue: '',
      parts: { day, month, year },
    };
  }

  const age = calculateAgeFromDateOfBirth(isoValue, today);
  if (age == null) {
    return {
      age: null,
      error: 'Date of birth cannot be in the future.',
      isBlank: false,
      isComplete: true,
      isValid: false,
      isoValue: '',
      parts: { day, month, year },
    };
  }

  return {
    age,
    error: null,
    isBlank: false,
    isComplete: true,
    isValid: true,
    isoValue,
    parts: { day, month, year },
  };
}
